import React, { useEffect, useState } from "react";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector";
import { fetchAllEmployees, exportEmployeesCSV, fetchAllCompanies } from "../../store/slices/userSlice";
import TopHeadding from "../../components/layout/TopHeadding";
import EmployeeListTable from "../../components/tables/EmployeListTable";
import plus_icon from "../../assets/images/plus_icon.svg";
import FilterTopBar from "../../components/navigation/FilterTopBar";
import SelectCommonModal from "../../components/popups/SelectCommonModal";
import { useNavigate } from "react-router-dom";

const Employes = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const EMPLOYEES_PAGE_KEY = "mainEmployeesListing";
  const MODAL_COMPANY_KEY = "modalCompanyList";

  const { user, role, exportCSVLoading } = useAppSelector((s) => s.user);

  const employeeList = useAppSelector((s) => s.user.listDataByKey[EMPLOYEES_PAGE_KEY] || []);
  const totalRecords = useAppSelector((s) => s.user.totalByKey[EMPLOYEES_PAGE_KEY] || 0);
  const companyListForModal = useAppSelector((s) => s.user.listDataByKey[MODAL_COMPANY_KEY] || []);

  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    payRange: [0, 1000000],
  });

  const buildCompanyParams = React.useCallback((extra = {}) => {
    const params = {
      startDate: filters.startDate ?? null,
      endDate: filters.endDate ?? null,
      status: filters.status,
      minPayAvg: filters.payRange[0],
      maxPayAvg: filters.payRange[1],
      ...extra,
    };

    if (search) {
      params.search = search;
    }

    if (role === "Client") {
      params.clientId = user.id;
    } else if (role === "Company") {
      params.companyId = user.id;
    }

    return params;
  }, [filters, search, role, user]);

  const params = role === "Client" ? { clientId: user?.id } : {};

  useEffect(() => {
    const maxPage = Math.ceil(totalRecords / pageSize) || 1;
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [totalRecords, page, pageSize]);

  useEffect(() => {
    if (!user?.id || !role) return;
    const fetchParams = buildCompanyParams({
      page: page,
      pageSize: pageSize,
    });
    const timeout = setTimeout(() => {
      dispatch(fetchAllEmployees({ params: fetchParams, key: EMPLOYEES_PAGE_KEY }));
    }, 400);
    return () => clearTimeout(timeout);
  }, [user, role, search, dispatch, page, filters, buildCompanyParams]);

  useEffect(() => {
    if (!showModal) return;
    dispatch(fetchAllCompanies({ params, key: MODAL_COMPANY_KEY }));
  }, [showModal, dispatch, params]);

  const handleExport = () => {
    const params = buildCompanyParams();
    dispatch(exportEmployeesCSV(params));
  };

  return (
    <div className="Companies">
      <TopHeadding
        title="Employee List"
        spanText={`(${totalRecords})`}
        subtitle=""
        showDatepicker={false}
        buttons={[
          {
            text: "Add Employee",
            className: role === "Company" && !user.isActive ? "btn_primary disabled" : "btn_primary",
            icon: plus_icon,
            disabled: role === "Company" && !user.isActive,
            onClick: () => {
              if (role === "Client")
                setShowModal(true);
              else if (role === "Company" && user.isActive)
                navigate("/addnewemployee", { state: { id: user?.id, from: "/employes" } });
            },
          },
        ]}
      />

      <div className="clint_table mt_30">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-12">
              <div className="white_box py_20 px_20">
                <FilterTopBar
                  search={search}
                  onSearchChange={setSearch}
                  placeholder="Search Employees"
                  showExport={true}
                  exportCSVLoading={exportCSVLoading}
                  onFilterChange={setFilters}
                  filterClassName="ms-auto"
                  customExportHandler={handleExport}
                  customExportLoading={exportCSVLoading}
                />

                <EmployeeListTable data={employeeList}
                  currentPage={page}
                  pageSize={pageSize}
                  totalRecords={totalRecords}
                  onPageChange={(pageNumber) => setPage(pageNumber)}
                  pageKey={EMPLOYEES_PAGE_KEY} />

              </div>
            </div>
          </div>
        </div>
      </div>
      {showModal && (
        <SelectCommonModal
          title="Please Select Company"
          data={companyListForModal}
          labelKey="companyName"
          valueKey="id"
          imageKey="logo"
          filterInactive={true}
          onClose={() => setShowModal(false)}
          onApply={(company) => navigate("/addnewemployee", { state: { id: company.id, from: "/employes" } })}
        />
      )}
    </div>
  );
};

export default Employes;
